// src/owner/components/FraudCard.jsx
import { useState } from "react";
import FraudDetailsModal from "./FraudDetailsModal";
import { API_BASE_URL } from "../../config/api";
import { authHeader } from "../../utils/authHeader";
import FRAUD_TYPES from "../../config/fraudTypes";

const FraudCard = ({
  fullName, fraudType, dlNo, location, phone,
  date, status = "Pending", fraud, onRefresh,
}) => {
  const [open,        setOpen]        = useState(false);
  const [editModal,   setEditModal]   = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [deleting,    setDeleting]    = useState(false);
  const [msg,         setMsg]         = useState("");
  const [fileKey,     setFileKey]     = useState(0);
  const [image,       setImage]       = useState(null);
  const [preview,     setPreview]     = useState(null);

  // Edit form — pre-filled with existing data
  const [form, setForm] = useState({});

  const canEdit   = ["pending", "rejected"].includes(status?.toLowerCase());
  const canDelete = ["pending", "rejected"].includes(status?.toLowerCase());

  // Pincode auto-fill
  const fetchLocation = async (pincode, cityKey, areaKey, stateKey) => {
    if (pincode.length !== 6) return;
    try {
      const res  = await fetch(`${API_BASE_URL}/api/location/pincode/${pincode}`);
      const data = await res.json();
      if (res.ok) {
        setForm(f => ({ ...f, [cityKey]: data.city || "", [areaKey]: data.area || "", [stateKey]: data.state || "" }));
      } else {
        // Clear fields if pincode not found
        setForm(f => ({ ...f, [cityKey]: "", [areaKey]: "", [stateKey]: "" }));
      }
    } catch {
      // Clear fields on error
      setForm(f => ({ ...f, [cityKey]: "", [areaKey]: "", [stateKey]: "" }));
    }
  };

  const openEdit = () => {
    // Pre-fill ALL fields from existing fraud data
    setForm({
      fullName:        fraud?.fullName            || "",
      phone:           fraud?.phoneNumbers?.[0]   || "",
      fraudType:       fraud?.fraudType           || "",
      aadhaarNumber:   fraud?.aadhaarNumber       || "",
      drivingLicense:  fraud?.drivingLicense      || "",
      fraudDate:       fraud?.fraudDate ? fraud.fraudDate.slice(0, 10) : "",
      fraudPincode:    fraud?.fraudPincode        || "",
      fraudCity:       fraud?.fraudCity           || "",
      fraudArea:       fraud?.fraudArea           || "",
      fraudState:      fraud?.fraudState          || "",
      aadhaarPincode:  fraud?.aadhaarPincode      || "",
      aadhaarCity:     fraud?.aadhaarCity         || "",
      aadhaarArea:     fraud?.aadhaarArea         || "",
      aadhaarState:    fraud?.aadhaarState        || "",
      description:     fraud?.description         || "",
    });
    setImage(null);
    setPreview(null);
    setFileKey(k => k + 1);
    setMsg("");
    setEditModal(true);
  };

  const handleEdit = async () => {
    if (!form.fullName?.trim()) { setMsg("❌ Full name is required"); return; }

    if (!form.phone?.trim()) {
      setMsg("❌ Phone number is required");
      return;
    }
    if (!/^[6-9]\d{9}$/.test(form.phone.trim())) {
      setMsg("❌ Phone must be exactly 10 digits and start with 6-9");
      return;
    }

    if (!form.fraudType) {
      setMsg("❌ Please select fraud type");
      return;
    }

    if (form.aadhaarNumber && !/^\d{12}$/.test(form.aadhaarNumber.trim())) {
      setMsg("❌ Aadhaar must be exactly 12 digits");
      return;
    }

    if (form.drivingLicense && !/^[A-Z0-9-]+$/.test(form.drivingLicense.trim())) {
      setMsg("❌ Driving License must be uppercase alphanumeric");
      return;
    }

    if (form.fraudPincode && !/^\d{6}$/.test(form.fraudPincode.trim())) {
      setMsg("❌ Fraud pincode must be 6 digits");
      return;
    }

    if (form.aadhaarPincode && !/^\d{6}$/.test(form.aadhaarPincode.trim())) {
      setMsg("❌ Aadhaar pincode must be 6 digits");
      return;
    }

    setSaving(true); setMsg("");
    try {
      const formData = new FormData();

      // Append all text fields
      Object.entries(form).forEach(([key, val]) => {
        formData.append(key, val || "");
      });

      // Append new image if selected
      if (image) formData.append("images", image);

      const res  = await fetch(`${API_BASE_URL}/api/fraud/${fraud._id}`, {
        method:  "PUT",
        headers: { Authorization: authHeader().Authorization },
        body:    formData,
      });
      const data = await res.json();
      if (res.ok) {
        setMsg("✅ " + data.message);
        setTimeout(() => { setEditModal(false); setMsg(""); onRefresh?.(); }, 1800);
      } else {
        setMsg("❌ " + data.message);
      }
    } catch {
      setMsg("❌ Update failed. Try again.");
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res  = await fetch(`${API_BASE_URL}/api/fraud/${fraud._id}`, {
        method:  "DELETE",
        headers: authHeader(),
      });
      const data = await res.json();
      if (res.ok) { setDeleteModal(false); onRefresh?.(); }
      else        { alert("❌ " + data.message); }
    } catch { alert("Delete failed. Try again."); }
    setDeleting(false);
  };

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  return (
    <>
      <div className="fraud-card compact">
        <div className="fraud-info">
          <div className="fraud-top">
            <h4>{fullName || "N/A"}</h4>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              {canEdit && (
                <button onClick={openEdit}
                  style={{ padding: "4px 10px", background: "#ede9fe", color: "#4318FF", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                  ✏️ Edit
                </button>
              )}
              {canDelete && (
                <button onClick={() => setDeleteModal(true)}
                  style={{ padding: "4px 10px", background: "#fee2e2", color: "#dc2626", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                  🗑️
                </button>
              )}
              <button className="view-more" onClick={() => setOpen(true)}>View More</button>
            </div>
          </div>
          <div className="fraud-meta compact">
            <div><span>Fraud Type</span><p>{fraudType || "-"}</p></div>
            <div><span>DL No</span><p>{dlNo || "-"}</p></div>
            <div><span>Location</span><p>{location || "-"}</p></div>
            <div><span>Date</span><p>{date ? new Date(date).toLocaleDateString() : "-"}</p></div>
            <div><span>Phone</span><p>{phone || "-"}</p></div>
          </div>
        </div>
        <div className={`fraud-status ${status.toLowerCase()}`}>Fraud {status}</div>
      </div>

      <FraudDetailsModal open={open} onClose={() => setOpen(false)} fraud={fraud} />

      {/* ── EDIT MODAL ── */}
      {editModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
          <div style={{ background: "#fff", borderRadius: 16, width: 620, maxWidth: "100%", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 8px 40px rgba(0,0,0,0.15)" }}>

            {/* Header */}
            <div style={{ padding: "20px 24px 0", borderBottom: "1px solid #f4f7fe", marginBottom: 16 }}>
              <h3 style={{ margin: "0 0 6px", color: "#1b2559", fontSize: 17 }}>✏️ Edit Fraud Report</h3>
              <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 8, padding: "8px 12px", marginBottom: 16, fontSize: 12, color: "#92400e" }}>
                ⚠️ After saving, status resets to <b>Pending</b> for admin review.
              </div>
            </div>

            <div style={{ padding: "0 24px 24px" }}>

              {/* Basic Details */}
              <div style={{ fontSize: 12, fontWeight: 700, color: "#4318FF", marginBottom: 10, textTransform: "uppercase", letterSpacing: 1 }}>
                Basic Details
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={lbl}>Full Name *</label>
                  <input style={inp} placeholder="Fraudster full name"
                    value={form.fullName} onChange={e => set("fullName", e.target.value)} />
                </div>
                <div>
                  <label style={lbl}>Phone Number *</label>
                  <input style={inp} placeholder="10-digit mobile number"
                    value={form.phone} onChange={e => set("phone", e.target.value)} />
                </div>
                <div>
                  <label style={lbl}>Fraud Type *</label>
                  <select style={inp} value={form.fraudType} onChange={e => set("fraudType", e.target.value)}>
                    <option value="">Select Fraud Type</option>
                    {FRAUD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Fraud Date</label>
                  <input type="date" style={inp} value={form.fraudDate} onChange={e => set("fraudDate", e.target.value)} />
                </div>
                <div>
                  <label style={lbl}>Aadhaar Number</label>
                  <input style={inp} placeholder="12-digit Aadhaar"
                    value={form.aadhaarNumber} onChange={e => set("aadhaarNumber", e.target.value)} />
                </div>
                <div>
                  <label style={lbl}>Driving License</label>
                  <input style={inp} placeholder="DL number"
                    value={form.drivingLicense} onChange={e => set("drivingLicense", e.target.value)} />
                </div>
              </div>

              {/* Fraud Location */}
              <div style={{ fontSize: 12, fontWeight: 700, color: "#4318FF", marginBottom: 10, textTransform: "uppercase", letterSpacing: 1 }}>
                Fraud Location
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={lbl}>Pincode</label>
                  <input style={inp} placeholder="6-digit pincode"
                    value={form.fraudPincode}
                    onChange={e => {
                      set("fraudPincode", e.target.value);
                      if (e.target.value.length === 6) {
                        set("fraudCity", ""); set("fraudArea", ""); set("fraudState", "");
                        fetchLocation(e.target.value, "fraudCity", "fraudArea", "fraudState");
                      } else {
                        set("fraudCity", ""); set("fraudArea", ""); set("fraudState", "");
                      }
                    }} />
                </div>
                <div>
                  <label style={lbl}>City</label>
                  <input style={inp} placeholder="Auto-filled from pincode"
                    value={form.fraudCity} onChange={e => set("fraudCity", e.target.value)} />
                </div>
                <div>
                  <label style={lbl}>Area</label>
                  <input style={inp} placeholder="Area / locality"
                    value={form.fraudArea} onChange={e => set("fraudArea", e.target.value)} />
                </div>
                <div>
                  <label style={lbl}>State</label>
                  <input style={inp} placeholder="State"
                    value={form.fraudState} onChange={e => set("fraudState", e.target.value)} />
                </div>
              </div>

              {/* Aadhaar Location */}
              <div style={{ fontSize: 12, fontWeight: 700, color: "#4318FF", marginBottom: 10, textTransform: "uppercase", letterSpacing: 1 }}>
                Aadhaar Location
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={lbl}>Pincode</label>
                  <input style={inp} placeholder="6-digit pincode"
                    value={form.aadhaarPincode}
                    onChange={e => {
                      set("aadhaarPincode", e.target.value);
                      if (e.target.value.length === 6) {
                        set("aadhaarCity", ""); set("aadhaarArea", ""); set("aadhaarState", "");
                        fetchLocation(e.target.value, "aadhaarCity", "aadhaarArea", "aadhaarState");
                      } else {
                        set("aadhaarCity", ""); set("aadhaarArea", ""); set("aadhaarState", "");
                      }
                    }} />
                </div>
                <div>
                  <label style={lbl}>City</label>
                  <input style={inp} placeholder="Auto-filled from pincode"
                    value={form.aadhaarCity} onChange={e => set("aadhaarCity", e.target.value)} />
                </div>
                <div>
                  <label style={lbl}>Area</label>
                  <input style={inp} placeholder="Area / locality"
                    value={form.aadhaarArea} onChange={e => set("aadhaarArea", e.target.value)} />
                </div>
                <div>
                  <label style={lbl}>State</label>
                  <input style={inp} placeholder="State"
                    value={form.aadhaarState} onChange={e => set("aadhaarState", e.target.value)} />
                </div>
              </div>

              {/* Description */}
              <div style={{ marginBottom: 16 }}>
                <label style={lbl}>Description</label>
                <textarea style={{ ...inp, height: 80, resize: "vertical" }}
                  placeholder="Additional details about the fraud..."
                  value={form.description} onChange={e => set("description", e.target.value)} />
              </div>

              {/* Image Upload */}
              <div style={{ marginBottom: 16 }}>
                <label style={lbl}>Update Fraud Proof Photo (optional)</label>
                {/* Show existing image */}
                {fraud?.proofImages?.[0] && !preview && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Current photo:</div>
                    <img src={fraud.proofImages[0]} alt="Current proof"
                      style={{ width: 100, height: 80, objectFit: "cover", borderRadius: 8, border: "1.5px solid #e0e5f2" }} />
                  </div>
                )}
                <div onClick={() => document.getElementById(`edit-img-${fraud._id}`).click()}
                  style={{ border: `2px dashed ${preview ? "#4318FF" : "#a3aed0"}`, borderRadius: 12,
                    padding: 16, textAlign: "center", cursor: "pointer", background: preview ? "#f0f4ff" : "#f9faff" }}>
                  {preview ? (
                    <img src={preview} alt="New preview"
                      style={{ maxHeight: 120, maxWidth: "100%", borderRadius: 8 }} />
                  ) : (
                    <>
                      <div style={{ fontSize: 28, marginBottom: 4 }}>📸</div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>
                        {fraud?.proofImages?.[0] ? "Click to replace photo" : "Click to upload photo"}
                      </div>
                      <div style={{ fontSize: 11, color: "#a3aed0", marginTop: 2 }}>JPG, PNG — max 2MB (auto-compressed)</div>
                    </>
                  )}
                </div>
                <input key={fileKey} id={`edit-img-${fraud._id}`} type="file" accept="image/*"
                  style={{ display: "none" }}
                  onChange={e => {
                    const f = e.target.files[0];
                    if (f) { setImage(f); setPreview(URL.createObjectURL(f)); }
                  }} />
                {preview && (
                  <button onClick={() => { setImage(null); setPreview(null); setFileKey(k => k + 1); }}
                    style={{ marginTop: 6, padding: "4px 10px", background: "#fee2e2", color: "#dc2626", border: "none", borderRadius: 6, fontSize: 11, cursor: "pointer" }}>
                    ✕ Remove new photo
                  </button>
                )}
              </div>

              {/* Message */}
              {msg && (
                <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 12, fontWeight: 600, fontSize: 13,
                  background: msg.startsWith("✅") ? "#dcfce7" : "#fee2e2",
                  color:      msg.startsWith("✅") ? "#16a34a"  : "#dc2626" }}>
                  {msg}
                </div>
              )}

              {/* Buttons */}
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button onClick={() => setEditModal(false)}
                  style={{ padding: "11px 22px", borderRadius: 8, border: "1.5px solid #e0e5f2", background: "#f4f7fe", cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                  Cancel
                </button>
                <button onClick={handleEdit} disabled={saving}
                  style={{ padding: "11px 26px", borderRadius: 8, border: "none", cursor: saving ? "not-allowed" : "pointer",
                    fontWeight: 700, fontSize: 13, color: "#fff", background: "#4318FF", opacity: saving ? 0.7 : 1 }}>
                  {saving ? "Saving..." : "💾 Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE MODAL ── */}
      {deleteModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: 400, maxWidth: "95vw" }}>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 48, marginBottom: 8 }}>🗑️</div>
              <h3 style={{ margin: 0, color: "#dc2626" }}>Delete Report?</h3>
              <p style={{ color: "#6b7280", fontSize: 13, marginTop: 8, lineHeight: 1.6 }}>
                Delete fraud report for <b>{fraud.fullName}</b>?<br/>
                <span style={{ color: "#dc2626" }}>This cannot be undone.</span>
              </p>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button onClick={() => setDeleteModal(false)}
                style={{ padding: "10px 24px", borderRadius: 8, border: "1.5px solid #e0e5f2", background: "#f4f7fe", cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                Cancel
              </button>
              <button onClick={handleDelete} disabled={deleting}
                style={{ padding: "10px 24px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13, color: "#fff", background: "#dc2626" }}>
                {deleting ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const lbl = { display: "block", fontSize: 11, fontWeight: 700, color: "#6b7280", marginBottom: 4 };
const inp = { width: "100%", padding: "9px 11px", borderRadius: 8, border: "1.5px solid #e0e5f2", fontSize: 13, outline: "none", boxSizing: "border-box", background: "#fff" };

export default FraudCard;